import { useEffect, useId, useRef, type KeyboardEvent } from "react";
import { Button, Segmented } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import type { KitchenSizeChoice } from "../lib/kitchenSizePreference";

/** What Tab can reach inside the start card. */
const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * «ابدأ الشاشة» — THE WALL BOARD'S ONE START TAP (T12, plan §4.11).
 *
 * A browser plays no sound until a person has touched the page, and that
 * unlock does not survive a reload — so a board that was meant to chime on
 * every new ticket stayed silent after every power cut, and nobody knew. The
 * overlay is shown on each page load of `/kitchen` (never the floor tab) until
 * it is tapped: the tap unlocks audio with a sample tone (`enableSound`) and
 * asks for the screen wake lock. Nothing is stored server-side.
 *
 * «متابعة بدون صوت» is the honest second door: the board, without the chime.
 * The overlay also carries the device's size choice (plan §4.12), because the
 * first minute at a new screen is exactly when someone is standing at it.
 *
 * It is MODAL: `aria-modal`, and the page makes the board behind it `inert`
 * while it shows, so focus and taps stay on the choice. `inert` blocks
 * interaction only — the board stays mounted and keeps reading and refreshing
 * underneath, so nothing is missed while it shows. Tab also wraps inside the
 * card itself, for a kitchen browser that does not support `inert`. Escape
 * does nothing: the choice is required.
 */
export function KitchenStartOverlay({
  sizeChoice,
  onSizeChange,
  onStart,
  onSilent,
}: {
  sizeChoice: KitchenSizeChoice;
  onSizeChange: (next: KitchenSizeChoice) => void;
  onStart: () => void;
  onSilent: () => void;
}) {
  const t = useTranslate();
  const titleId = useId();
  const bodyId = useId();
  const startRef = useRef<HTMLButtonElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    startRef.current?.focus();
  }, []);
  /* Tab and Shift+Tab wrap between the card's first and last control. */
  const keepFocusInside = (event: KeyboardEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (event.key !== "Tab" || !card) return;
    const focusable = [...card.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (node) => node.tabIndex >= 0,
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    const inside = active instanceof Node && card.contains(active);
    if (event.shiftKey && (!inside || active === first)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (!inside || active === last)) {
      event.preventDefault();
      first.focus();
    }
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      data-ui="kitchen-start"
      data-testid="kitchen-start"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onKeyDown={keepFocusInside}
    >
      <div
        ref={cardRef}
        data-ui="kitchen-start-card"
        className="flex w-full max-w-[560px] flex-col items-stretch gap-5 text-center"
      >
        <h2 id={titleId} data-ui="kitchen-start-title">
          {t((m) => m.kitchen.startTitle)}
        </h2>
        <p id={bodyId} data-ui="kitchen-start-body">
          {t((m) => m.kitchen.startBody)}
        </p>
        <Button
          ref={startRef}
          variant="primary"
          size="lg"
          data-testid="kitchen-start-action"
          onClick={onStart}
        >
          {t((m) => m.kitchen.startAction)}
        </Button>
        <Button variant="outline" size="lg" data-testid="kitchen-start-silent" onClick={onSilent}>
          {t((m) => m.kitchen.startSilent)}
        </Button>
        <div data-ui="kitchen-start-size" className="flex flex-col items-center gap-2">
          <span data-ui="kitchen-start-size-label">{t((m) => m.kitchen.sizeLabel)}</span>
          <KitchenSizeSwitch value={sizeChoice} onChange={onSizeChange} />
        </div>
      </div>
    </div>
  );
}

/** The device's size choice — shared by the start overlay and the screen sheet. */
export function KitchenSizeSwitch({
  value,
  onChange,
}: {
  value: KitchenSizeChoice;
  onChange: (next: KitchenSizeChoice) => void;
}) {
  const t = useTranslate();
  return (
    <Segmented<KitchenSizeChoice>
      value={value}
      onChange={onChange}
      ariaLabel={t((m) => m.kitchen.sizeLabel)}
      options={[
        { value: "auto", label: t((m) => m.kitchen.sizeAuto) },
        { value: "touch", label: t((m) => m.kitchen.sizeTouch) },
        { value: "wall", label: t((m) => m.kitchen.sizeWall) },
      ]}
    />
  );
}
